import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { getColorByIndex } from "@/utils/colors";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { getChartSeriesColors } from "@/components/chart/styles/colors";

// Define interface for extended asset info
export interface ExtendedAssetInfo {
  displayName: string;
  resourceId: string;
  resourceType: string;
  streamType:
    | "total_return"
    | "TOTAL_RETURN"
    | "price"
    | "PRICE"
    | "gross_return"
    | "GROSS_RETURN"
    | "net_return"
    | "NET_RETURN"
    | "default";
}

interface AssetManagerParams {
  initialAssets?: string[];
  initialDisplayNames?: Record<string, string>;
  initialAssetColors?: Record<string, string>;
  initialAssetMetadata?: Record<string, ExtendedAssetInfo>;
  defaultResourceType?: "product" | "portfolio" | "benchmark" | "index";
  /** Optional preferred palette to use for default colors (e.g., chart palette) */
  preferredPalette?: string[];
  allowAssetToggle?: boolean;
}

export const useAssetManager = ({
  initialAssets = [],
  initialDisplayNames = {},
  initialAssetColors = {},
  initialAssetMetadata = {},
  defaultResourceType = "product",
  preferredPalette = [],
  allowAssetToggle = true,
}: AssetManagerParams = {}) => {
  // Get company colors
  const { chartColors: companyChartColors } = useCompanyColors();

  // State for selected assets and their metadata
  const [selectedAssets, setSelectedAssets] = useState<string[]>(initialAssets);
  const [assetDisplayNames, setAssetDisplayNames] =
    useState<Record<string, string>>(initialDisplayNames);
  const [assetMetadata, setAssetMetadata] = useState<
    Record<string, ExtendedAssetInfo>
  >(initialAssetMetadata);
  const [assetColorOverrides, setAssetColorOverrides] = useState<
    Record<string, string>
  >({});

  // Sync with initialAssets when it changes (for embedded components)
  // Use a ref to track the last initialAssets to avoid unnecessary updates
  const lastInitialAssetsRef = useRef<string>('');
  useEffect(() => {
    const currentKey = initialAssets.join(',');
    if (currentKey !== lastInitialAssetsRef.current) {
      lastInitialAssetsRef.current = currentKey;
      // Always sync when initialAssets changes (even if empty, to clear selection)
      setSelectedAssets([...initialAssets]);
    }
  }, [initialAssets]);

  // Sync display names when they change
  useEffect(() => {
    if (Object.keys(initialDisplayNames).length > 0) {
      setAssetDisplayNames((prev) => ({ ...prev, ...initialDisplayNames }));
    }
  }, [initialDisplayNames]);

  // Sync metadata when it changes
  useEffect(() => {
    if (Object.keys(initialAssetMetadata).length > 0) {
      setAssetMetadata((prev) => ({ ...prev, ...initialAssetMetadata }));
    }
  }, [initialAssetMetadata]);

  // Seed metadata for initial assets when embed contexts pass defaultResourceType
  // but not explicit initialAssetMetadata (e.g. Portfolio tab).
  useEffect(() => {
    if (initialAssets.length === 0) {
      return;
    }

    setAssetMetadata((prev) => {
      let changed = false;
      const next = { ...prev };

      initialAssets.forEach((assetId) => {
        if (next[assetId]) {
          return;
        }

        next[assetId] = {
          displayName: initialDisplayNames[assetId] || assetId,
          resourceId: assetId,
          resourceType: defaultResourceType,
          streamType: "default",
        };
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [initialAssets, initialDisplayNames, defaultResourceType]);

  const setAssetColor = useCallback((assetId: string, color: string) => {
    setAssetColorOverrides((prev) => ({
      ...prev,
      [assetId]: color,
    }));
  }, []);

  const setAssetColors = useCallback((colors: Record<string, string>) => {
    setAssetColorOverrides(colors);
  }, []);

  // Toggle asset selection
  const toggleAsset = useCallback(
    (asset: string | undefined | null, displayName?: string) => {
      if (!allowAssetToggle) return;

      if (!asset) {
        return;
      }

      const assetId = String(asset);
      if (assetId.trim() === "") {
        return;
      }

      // Check if this asset is already selected
      const isSelected = selectedAssets.includes(assetId);

      setSelectedAssets((prev) => {
        if (prev.includes(assetId)) {
          return prev.filter((a) => a !== assetId);
        }
        return [...prev, assetId];
      });

      if (displayName) {
        // Check if the display name contains metadata (in format "displayName:::resourceId:::resourceType:::streamType")
        if (displayName.includes(":::")) {
          const [actualDisplayName, resourceId, resourceType, streamType] =
            displayName.split(":::");

          // Update display names with just the actual display name part
          setAssetDisplayNames((prev) => ({
            ...prev,
            [assetId]: actualDisplayName,
          }));

          // Update asset metadata with the complete information
          setAssetMetadata((prev) => ({
            ...prev,
            [assetId]: {
              displayName: actualDisplayName,
              resourceId: resourceId || assetId,
              resourceType: resourceType || defaultResourceType,
              streamType: (streamType || "default") as
                | "total_return"
                | "TOTAL_RETURN"
                | "price"
                | "PRICE"
                | "gross_return"
                | "GROSS_RETURN"
                | "net_return"
                | "NET_RETURN"
                | "default",
            },
          }));
        } else {
          // Regular display name without metadata
          setAssetDisplayNames((prev) => ({
            ...prev,
            [assetId]: displayName,
          }));
        }
      }

      // Return whether the asset is now selected or not
      return !isSelected;
    },
    [allowAssetToggle, selectedAssets, defaultResourceType]
  );

  // Batch set assets and metadata
  const setAssets = useCallback(
    (assets: string[], metadata?: Record<string, ExtendedAssetInfo>) => {
      setSelectedAssets(assets);

      if (metadata) {
        setAssetMetadata(metadata);

        // Extract display names from metadata
        const displayNames: Record<string, string> = {};
        Object.entries(metadata).forEach(([id, info]) => {
          displayNames[id] = info.displayName;
        });
        setAssetDisplayNames(displayNames);
      }
    },
    []
  );

  // Create a consistent color mapping for assets using company colors
  const effectiveAssetColors = useMemo(() => {
    // Prefer a caller-provided palette if supplied, otherwise fall back to company colors
    const chartColors =
      preferredPalette && preferredPalette.length > 0
        ? preferredPalette
        : getChartSeriesColors(companyChartColors);

    const colors: Record<string, string> = {};
    selectedAssets.forEach((asset, index) => {
      if (assetColorOverrides[asset]) {
        colors[asset] = assetColorOverrides[asset];
      } else if (chartColors.length > 0) {
        colors[asset] = chartColors[index % chartColors.length];
      }
    });

    return colors;
  }, [selectedAssets, companyChartColors, assetColorOverrides, preferredPalette]);

  return {
    selectedAssets,
    assetDisplayNames,
    assetMetadata,
    assetColors: effectiveAssetColors,
    toggleAsset,
    setAssets,
    setAssetMetadata,
    setAssetColor,
    setAssetColors,
  };
};
